import {useQuery} from '@tanstack/react-query';

import {splitImageOrderBy} from '~utils/miscUtils';
import {
  useConfusionCellFromQueryParams,
  useEvaluationCellFromQueryParams,
  useSimilaritySearchFromQueryParams,
} from '~hooks/imagesOverview';
import {useIsUnstableGallery} from '~hooks/useIsUnstableGallery';
import {ImageFilters} from '~redux/types/images';
import {selectMetadata} from '~redux/reducers/filtersReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {useLabelIds} from '~components/models/model.hooks';
import {
  getLabelDistributionCounts,
  LabelDistributionCounts,
} from '~components/statistics/label-distribution/chartUtils';
import {MAX_IMAGES_TOTAL} from 'src/constants/constants';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {
  getGroupedImagesData,
  getImagesData,
  getImagesIds,
  ImageFiltersPayload,
  ImagesDataPayload,
  SortAndCountArgs,
} from './images';

export const ImageQueryKeys = {
  all: ['images'] as const,
  imagesData: (projectId: string, params: ImagesDataPayload) => [...ImageQueryKeys.all, projectId, params] as const,
  annotationHistory: (imageId?: string) => [...ImageQueryKeys.all, 'annotationHistory', imageId] as const,
  imagesDataFilter: (projectId: string, params?: ImageFiltersPayload) =>
    [...ImageQueryKeys.all, projectId, params] as const,
  imagesDataFilterPaginated: (
    projectId: string,
    filters: ImageFiltersPayload | undefined,
    sorting: SortAndCountArgs | undefined,
    targetPage: number | undefined,
  ) => [...ImageQueryKeys.imagesDataFilter(projectId, filters), sorting, targetPage] as const,
  imagesDataModel: (projectId: string, params?: Pick<ImageFiltersPayload, 'humanAnnotationLabels'>) =>
    [...ImageQueryKeys.all, projectId, params] as const,
  latestImage: (projectId: string, params?: ImagesDataPayload) =>
    [...ImageQueryKeys.all, projectId, params, 'latestImage'] as const,
  labelInstancesData: (projectId: string, params: ImagesDataPayload) =>
    [...ImageQueryKeys.all, projectId, params] as const,
  imagesPerIds: (projectId: string, imageIds: string[]) => [...ImageQueryKeys.all, projectId, imageIds] as const,
  imagesIds: (projectId: string, filters: ImageFiltersPayload) => [...ImageQueryKeys.all, projectId, filters] as const,
  groupedImages: (projectId: string, params?: ImagesDataPayload) =>
    [...ImageQueryKeys.all, projectId, 'groupedImages', params] as const,
};

export function useCurrentAnnotationHistoryQuery({imageId, modelId}: {imageId?: string; modelId?: string}) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  const confusionCell = useConfusionCellFromQueryParams();
  const evaluationCell = useEvaluationCellFromQueryParams();
  const withUnstablePredictions = useIsUnstableGallery();
  const similaritySearch = useSimilaritySearchFromQueryParams();

  const payload: ImagesDataPayload = {
    filters: {
      ...(confusionCell && {confusionCell}),
      ...(evaluationCell && {evaluationCell}),
      ...(withUnstablePredictions && {withUnstablePredictions}),
      ...(similaritySearch && {similaritySearch}),
      objectId: imageId,
      includeAnnotationHistory: true,
    },
    pagination: {count: 1},
  };

  return useQuery({
    queryKey: ImageQueryKeys.annotationHistory(imageId),
    queryFn: ({signal}) => getImagesData(projectId, payload, signal, metadata),
    enabled: modelId !== undefined && imageId !== undefined,
    staleTime: 1000 * 60, // 1 minute
    select: (imagesData) => {
      if (!modelId || !imagesData.data[0]?.humanAnnotationHistory) {
        return [];
      }
      return imagesData.data[0]?.humanAnnotationHistory![modelId] || [];
    },
  });
}

/**
 * This query returns the latest image for a given project.
 */
export function useLatestImageQuery({
  projectId,
  params,
  staleTime,
  enabled = true,
}: {
  projectId: string;
  params?: ImagesDataPayload;
  staleTime?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ImageQueryKeys.latestImage(projectId, params),
    queryFn: ({signal}) =>
      getImagesData(
        projectId,
        {
          ...(params?.filters && {filters: params.filters}),
          pagination: {count: 1, sortBy: 'TIMESTAMP', sortOrder: 'DESC'},
        },
        signal,
      ),
    staleTime: staleTime ?? 1000 * 60 * 10,
    select: (imagesData) => imagesData.data[0],
    enabled: enabled,
  });
}

/**
 * This query is used to get the label distribution counts for the current model, which are part of the model insights.
 * Note: this uses the /images endpoint.
 *
 * @param productTypes The product types to filter the label distribution counts by.
 * @returns The label distribution counts.
 */
export function useLabelDistributionCountsQuery({productTypes}: {productTypes: string[]}) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  const {currentModel} = useCurrentModel();
  const labelIds = useLabelIds({from: 'current-model'});

  const payload: ImagesDataPayload = {
    filters: {
      humanAnnotationLabels: labelIds,
    },
    pagination: {
      count: MAX_IMAGES_TOTAL,
    },
  };

  return useQuery({
    queryKey: ImageQueryKeys.imagesData(projectId, payload),
    queryFn: ({signal}) => getImagesData(projectId, payload, signal, metadata),
    enabled: labelIds?.length > 0,
    staleTime: 1000 * 60,
    select: (imagesData): LabelDistributionCounts => {
      if (currentModel === undefined) {
        return {
          imageCounts: {},
          instanceCounts: {},
        };
      }
      return getLabelDistributionCounts({
        imageData: imagesData.data,
        currentModel,
        productTypes,
      });
    },
  });
}

/**
 * This query returns the images per list of ids for a given project.
 */
export function useImagesPerIds(imageIds: string[] = []) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  return useQuery({
    queryKey: ImageQueryKeys.imagesPerIds(projectId, imageIds),
    queryFn: ({signal}) =>
      getImagesData(
        projectId,
        {
          filters: {objectIds: imageIds},
          pagination: {count: imageIds.length}, // Fetch all images at once
        },
        signal,
        metadata,
      ),
    enabled: !!imageIds.length,
  });
}

/**
 * This query returns the images ids for given filters.
 */
export function useImagesIds(imageFilters: ImageFilters | undefined, enabled = true) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);

  const {orderBy, ...filters} = imageFilters || {};
  const pagination = orderBy ? splitImageOrderBy(orderBy) : undefined;

  return useQuery({
    queryKey: ImageQueryKeys.imagesIds(projectId, imageFilters ?? {}),
    queryFn: ({signal}) => getImagesIds(projectId, filters, pagination, signal, metadata),
    enabled,
  });
}

export function useImagesDataQuery(params: ImagesDataPayload, {staleTime = 1000 * 60}: {staleTime?: number}) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  return useQuery({
    queryKey: ImageQueryKeys.imagesData(projectId, params),
    queryFn: ({signal}) => getImagesData(projectId, params, signal, metadata),
    staleTime: staleTime,
  });
}

export function useGroupedImagesQuery(
  params: ImagesDataPayload,
  {staleTime = 1000 * 60, enabled = true}: {staleTime?: number; enabled?: boolean} = {},
) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  return useQuery({
    queryKey: ImageQueryKeys.groupedImages(projectId, params),
    queryFn: ({signal}) => getGroupedImagesData(projectId, params, signal, metadata),
    staleTime,
    enabled,
  });
}
